/**
 * Bidirectional reducers for event-log replay.
 *
 * Per-event undo delta architecture: the server enriches each state-changing event with
 * payload.prev containing only the fields that event type modifies. backwardReducer reads
 * payload.prev and restores the pre-event state. Events without prev (e.g. logged before
 * enrichment, or boundary events) leave state unchanged. computeStateAtEventIndex runs
 * backward reduction from the anchor in O(N) for the event range.
 */

import { cloneDeep } from 'lodash';

import { EventLogEntry, EventType } from '~/server/src/dtos';
import { type Field, type Horse, type Player, StateMachineValue } from '~/server/src/tmmodel';

/** Inner game state (players, horses, round, turnorder, etc.) - same shape as controller.getGame() */
export type GameStateInner = Record<string, unknown>;

/**
 * Replay-derived state at an event index: GameStateInner plus known fields used by replay/spectate UI.
 * Same runtime shape as Game from tmmodel when produced by computeStateAtEventIndex.
 */
export type GameStateAtEvent = GameStateInner & {
  currentState?: StateMachineValue;
  horses?: Horse[];
  players?: Player[];
};

/** Sparse cache for replay state; invalidated when eventLog or anchor changes. */
const CACHE_INTERVAL = 50;
let replayCache: Map<number, GameStateAtEvent> | null = null;
let replayCacheEventLog: EventLogEntry[] | null = null;
let replayCacheAnchor: GameStateInner | null = null;

/**
 * Compute game state at a given event index.
 * - index === eventLog.length: return currentState (anchor).
 * - index < eventLog.length: apply backward reducer (eventLog.length - index) times from currentState,
 *   with events in reverse order (from last event down to event[index]).
 * Uses a sparse cache (every CACHE_INTERVAL events) to reduce work when navigating to steps far in the past.
 */
export function computeStateAtEventIndex(
  currentState: GameStateInner,
  eventLog: EventLogEntry[],
  eventIndex: number,
): GameStateAtEvent {
  if (eventIndex >= eventLog.length) {
    return cloneDeep(currentState);
  }
  if (
    replayCache === null ||
    eventLog !== replayCacheEventLog ||
    currentState !== replayCacheAnchor
  ) {
    replayCache = new Map();
    replayCacheEventLog = eventLog;
    replayCacheAnchor = currentState;
  }
  const cache = replayCache;

  const cached = cache.get(eventIndex);
  if (cached !== undefined) {
    return cloneDeep(cached);
  }

  let startIndex = eventLog.length;
  let state: GameStateAtEvent = cloneDeep(currentState);
  for (const [idx, cachedState] of cache.entries()) {
    if (idx >= eventIndex && idx < startIndex) {
      startIndex = idx;
      state = cachedState;
    }
  }
  if (startIndex === eventLog.length) {
    state = cloneDeep(currentState);
  }

  for (let i = startIndex - 1; i >= eventIndex; i--) {
    state = backwardReducer(state, eventLog[i]);
    if (i % CACHE_INTERVAL === 0) {
      cache.set(i, state);
    }
  }
  cache.set(eventIndex, state);
  return cloneDeep(state);
}

/**
 * Backward reducer: (state, event) => previousState.
 * Apply inverse of the event to go back in time.
 * Non-invertible events (NEWGAME, JOINGAME) return state unchanged (backward stops at them in practice).
 */
export function backwardReducer(state: GameStateInner, entry: EventLogEntry): GameStateInner {
  const next = cloneDeep(state);
  const { eventType } = entry;

  switch (eventType) {
    case EventType.GETGAMES:
    case EventType.RESTOREGAME:
    case EventType.REJOINGAME:
    case EventType.GETTOURNAMENTS:
    case EventType.NEWMESSAGE:
    case EventType.SENDMESSAGE:
    case EventType.LEAVEGAME:
    case EventType.LEAVEGAMEOPP:
    case EventType.JOINTOURNAMENT:
    case EventType.TM_GAMECREATED:
    case EventType.TM_REJOINGAME:
    case EventType.UNKNOWN:
    case EventType.MERGECARDSOPP:
    case EventType.ADDBONUSCARDSOPP:
      return next;

    case EventType.NEWGAME:
    case EventType.JOINGAME:
      return next;

    case EventType.JOINGAMEOPP:
    case EventType.STARTGAME:
    case EventType.STARTROUND:
    case EventType.MERGECARDS:
    case EventType.STARTTURN:
    case EventType.ADDBONUSCARDS:
    case EventType.SELECTMOVEOPP:
    case EventType.SELECTDICES:
    case EventType.SELECTDICESOPP:
    case EventType.ENDTURN:
    case EventType.ENDROUND:
    case EventType.ENDGAME:
      return backwardReducerForStateChanging(next, entry);
    default:
      return next;
  }
}

/**
 * Per-event backward reducer using undo deltas from payload.prev.
 *
 * The server enriches each event payload with a `prev` object containing only
 * the specific fields that event type modified. This function reads payload.prev
 * and restores the pre-event state for each event type.
 *
 * Backward compatibility: events logged before prev enrichment have no payload.prev.
 * For those, the function returns state unchanged.
 *
 * Supported event types and what they reverse:
 * - SELECTMOVEOPP: horse position, dropped/finished, cards, turnpos, turnorder
 * - SELECTDICESOPP: turndicescur
 * - SELECTDICES: turndices array
 * - STARTROUND: all horses' dropped/finished flags
 * - MERGECARDS/MERGECARDSOPP: horse cards, turnpos
 * - ADDBONUSCARDS/ADDBONUSCARDSOPP: horse bonuscards, cards
 * - ENDTURN: turntype, turndiceplayerid, turnpos, turnorder
 * - ENDROUND: round, turn fields, horse positions/ranks/handi/points/finished/dropped
 * - ENDGAME: currentState
 */
function backwardReducerForStateChanging(
  state: GameStateInner,
  entry: EventLogEntry,
): GameStateInner {
  const prev = entry.payload?.prev as Record<string, unknown> | undefined;
  if (!prev) return state;

  const { eventType } = entry;
  const horses = state.horses as Horse[] | undefined;

  switch (eventType) {
    case EventType.SELECTMOVEOPP: {
      const idx = (prev.horseIndex ?? entry.payload?.horseid) as number | undefined;
      if (idx != null && horses?.[idx]) {
        const horseField = prev.horseField as {
          pos: number;
          prog: number;
          x: number;
          y: number;
        } | null;
        if (horseField) {
          const currentField = horses[idx].field;
          if (currentField) {
            Object.assign(currentField, horseField);
          } else {
            horses[idx].field = {
              ...horseField,
              lane: 0,
              nf: -1,
              nl: -1,
              nr: -1,
              hurdle: false,
            } as Field;
          }
        } else {
          horses[idx].field = null;
        }
        if (prev.horseDropped !== undefined) horses[idx].dropped = prev.horseDropped as boolean;
        if (prev.horseFinished !== undefined) horses[idx].finished = prev.horseFinished as boolean;
        if (prev.horseCards) horses[idx].cards = (prev.horseCards as number[][]).map((c) => [...c]);
      }
      if (prev.turnpos !== undefined) state.turnpos = prev.turnpos as number;
      if (prev.turnorder) state.turnorder = [...(prev.turnorder as number[])];
      return state;
    }
    case EventType.SELECTDICESOPP: {
      if (prev.turndicescur !== undefined) state.turndicescur = prev.turndicescur as number;
      return state;
    }
    case EventType.SELECTDICES: {
      if (prev.turndices) state.turndices = [...(prev.turndices as number[])];
      return state;
    }
    case EventType.STARTROUND: {
      if (prev.horsesDropped && horses) {
        (prev.horsesDropped as boolean[]).forEach((v, i) => {
          if (v !== undefined && horses[i]) horses[i].dropped = v;
        });
      }
      if (prev.horsesFinished && horses) {
        (prev.horsesFinished as boolean[]).forEach((v, i) => {
          if (v !== undefined && horses[i]) horses[i].finished = v;
        });
      }
      return state;
    }
    case EventType.MERGECARDS:
    case EventType.MERGECARDSOPP: {
      const idx = (prev.horseIndex ?? entry.payload?.horseid) as number | undefined;
      if (idx != null && horses?.[idx] && prev.horseCards) {
        horses[idx].cards = (prev.horseCards as number[][]).map((c) => [...c]);
      }
      if (prev.turnpos !== undefined) state.turnpos = prev.turnpos as number;
      return state;
    }
    case EventType.ADDBONUSCARDS:
    case EventType.ADDBONUSCARDSOPP: {
      const idx = (prev.horseIndex ?? entry.payload?.horseid) as number | undefined;
      if (idx != null && horses?.[idx]) {
        if (prev.horseBonuscards !== undefined)
          horses[idx].bonuscards = prev.horseBonuscards as number;
        if (prev.horseCards) horses[idx].cards = (prev.horseCards as number[][]).map((c) => [...c]);
      }
      return state;
    }
    case EventType.ENDTURN: {
      if (prev.turntype !== undefined) state.turntype = prev.turntype as number;
      if (prev.turndiceplayerid !== undefined)
        state.turndiceplayerid = prev.turndiceplayerid as number;
      if (prev.turnpos !== undefined) state.turnpos = prev.turnpos as number;
      if (prev.turnorder) state.turnorder = [...(prev.turnorder as number[])];
      return state;
    }
    case EventType.ENDROUND: {
      if (prev.round !== undefined) state.round = prev.round as number;
      if (prev.turntype !== undefined) state.turntype = prev.turntype as number;
      if (prev.turndiceplayerid !== undefined)
        state.turndiceplayerid = prev.turndiceplayerid as number;
      if (prev.turnpos !== undefined) state.turnpos = prev.turnpos as number;
      if (prev.turnorder) state.turnorder = [...(prev.turnorder as number[])];
      const horseFields = prev.horseFields as
        | Array<{ pos: number; prog: number; x: number; y: number } | null>
        | undefined;
      if (horseFields && horses) {
        horses.forEach((h, i) => {
          const pf = horseFields[i];
          if (pf) {
            if (h.field) Object.assign(h.field, pf);
            else
              h.field = {
                ...pf,
                lane: 0,
                nf: -1,
                nl: -1,
                nr: -1,
                hurdle: false,
              } as Field;
          } else {
            h.field = null;
          }
        });
      }
      const horsesRanks = prev.horsesRanks as
        | Array<{ rank: number; totalrank: number }>
        | undefined;
      if (horsesRanks && horses) {
        horses.forEach((h, i) => {
          const r = horsesRanks[i];
          if (r) {
            h.rank = r.rank;
            h.totalrank = r.totalrank;
          }
        });
      }
      if (prev.horsesHandi && horses) {
        (prev.horsesHandi as number[]).forEach((v, i) => {
          if (v !== undefined && horses[i]) horses[i].handi = v;
        });
      }
      if (prev.horsesPoints && horses) {
        (prev.horsesPoints as number[][]).forEach((v, i) => {
          if (v && horses[i]) horses[i].points = [...v];
        });
      }
      if (prev.horsesFinished && horses) {
        (prev.horsesFinished as boolean[]).forEach((v, i) => {
          if (v !== undefined && horses[i]) horses[i].finished = v;
        });
      }
      if (prev.horsesDropped && horses) {
        (prev.horsesDropped as boolean[]).forEach((v, i) => {
          if (v !== undefined && horses[i]) horses[i].dropped = v;
        });
      }
      return state;
    }
    case EventType.ENDGAME: {
      if (prev.currentState !== undefined) state.currentState = prev.currentState as number;
      return state;
    }
    default:
      return state;
  }
}

/**
 * Forward reducer: (state, event) => nextState.
 * Used when stepping forward from a past state.
 */
export function forwardReducer(state: GameStateInner, entry: EventLogEntry): GameStateInner {
  const next = cloneDeep(state);
  const { eventType } = entry;
  switch (eventType) {
    case EventType.GETGAMES:
    case EventType.RESTOREGAME:
    case EventType.REJOINGAME:
    case EventType.GETTOURNAMENTS:
    case EventType.NEWMESSAGE:
    case EventType.SENDMESSAGE:
    case EventType.LEAVEGAME:
    case EventType.LEAVEGAMEOPP:
    case EventType.JOINTOURNAMENT:
    case EventType.TM_GAMECREATED:
    case EventType.TM_REJOINGAME:
    case EventType.UNKNOWN:
      return next;
    default:
      return next;
  }
}
